import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  TextField,
  Chip,
  Tooltip,
  CircularProgress,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Stack,
  Collapse,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Print as PrintIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  CalendarToday,
  Business,
} from "@mui/icons-material";
import {
  freightPayListReports,
  paymentPrint,
} from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDebounce } from "../../../Hooks/useDebounce";
import { T } from "../../../theme";

const sq = { borderRadius: "4px !important" };

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryBg,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};
const dangerBtn = {
  ...sq,
  border: `1.5px solid ${T.redBorder}`,
  bgcolor: T.redBg,
  color: T.red,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fee2e2", borderColor: T.red, boxShadow: "none" },
};
const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
};
const iconBtn = { ...ghostBtn, minWidth: 0, px: 1 };

const filterInputSx = {
  "& .MuiInputBase-root": { height: 34, ...sq, bgcolor: T.primaryBg },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
  "& .Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
};

// ── Helpers ────────────────────────────────────────────────────────
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";
const formatCurrency = (a) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(a || 0);

// ── StatCard ───────────────────────────────────────────────────────
const StatCard = ({ title, value, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 1, sm: 1.5 },
      textAlign: "center",
      ...sq,
      border: `1.5px solid ${color}40`,
      borderTop: `3px solid ${color}`,
      bgcolor: `${color}08`,
      boxShadow: "none",
      minWidth: 0,
    }}
  >
    <Typography
      sx={{
        fontSize: { xs: "0.55rem", sm: "0.65rem" },
        color: T.textSub,
        lineHeight: 1.2,
        mb: 0.3,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.03em",
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontSize: { xs: "0.72rem", sm: "1rem" },
        fontWeight: 700,
        color,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "block",
      }}
      title={value}
    >
      {value}
    </Typography>
  </Paper>
);

// ── ActionButton ───────────────────────────────────────────────────
const ActionButton = ({ tip, icon, color, isView, isPrint, onClick }) => {
  const c = isView ? "#1976d2" : isPrint ? T.warning : color || T.primary;
  const bg = isView ? "#e3f2fd" : isPrint ? T.warningBg : `${c}14`;
  const hBg = isView ? "#bbdefb" : isPrint ? "#fef3c7" : `${c}28`;

  return (
    <Tooltip title={tip} arrow>
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          width: 20,
          height: 20,
          ...sq,
          bgcolor: bg,
          color: c,
          border: `1px solid ${isView ? "#bbdefb" : isPrint ? T.warningBg : `${c}30`}`,
          transition: "all 0.18s",
          "&:hover": {
            bgcolor: hBg,
            transform: "translateY(-1px)",
          },
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 12 } })}
      </IconButton>
    </Tooltip>
  );
};

// ── LabelValue ─────────────────────────────────────────────────────
const LabelValue = ({ label, value, color }) => (
  <Box>
    <Typography
      sx={{
        fontSize: "0.58rem",
        color: T.textMuted,
        fontWeight: 600,
        textTransform: "uppercase",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: "0.7rem", fontWeight: 600, color: color || T.text }}
    >
      {value}
    </Typography>
  </Box>
);

const getLRNumbers = (row) =>
  [
    ...new Set(row.freights?.map((f) => f.lr_number).filter(Boolean) || []),
  ].join(", ") || "—";

const getPartyNames = (row) =>
  [
    ...new Set(row.freights?.map((f) => f.party_name).filter(Boolean) || []),
  ].join(", ") || "—";

const getLRDisplay = (row) => {
  const lrs = [
    ...new Set(row.freights?.map((f) => f.lr_number).filter(Boolean) || []),
  ];
  if (!lrs.length) return "—";
  return lrs.length === 1 ? lrs[0] : `${lrs[0]} +${lrs.length - 1}`;
};

// ── MobilePaymentCard ──────────────────────────────────────────────
const MobilePaymentCard = ({
  row,
  idx,
  expanded,
  onToggle,
  onView,
  onPrint,
}) => {
  const isSettled = row.is_settlement === true;
  const isNegative = row.rem_amount < 0;
  const accentColor = isSettled ? T.success : isNegative ? T.red : T.primary;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        ...sq,
        border: `1.5px solid ${expanded ? T.primary : T.border}`,
        bgcolor: T.white,
        overflow: "hidden",
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: expanded ? "0 2px 12px rgba(79,70,229,0.1)" : "none",
      }}
    >
      {/* Card Header */}
      <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
        {/* Icon column */}
        <Box
          sx={{
            width: 58,
            flexShrink: 0,
            bgcolor: expanded ? T.primary : T.primaryBg,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRight: `1.5px solid ${expanded ? T.primary : T.border}`,
            transition: "background-color 0.2s",
            gap: 0.25,
          }}
        >
          <PaymentIcon
            sx={{ fontSize: 20, color: expanded ? T.white : T.primary }}
          />
          <Typography
            sx={{
              fontSize: "0.46rem",
              fontWeight: 700,
              color: expanded ? T.white : T.primary,
            }}
          >
            #{idx + 1}
          </Typography>
        </Box>

        {/* Main info */}
        <Box
          sx={{
            flex: 1,
            px: 1.2,
            py: 0.75,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.78rem",
              color: T.text,
              lineHeight: 1.2,
              mb: 0.15,
            }}
          >
            {getPartyNames(row)}
          </Typography>

          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.15 }}
          >
            <ReceiptIcon sx={{ fontSize: 10, color: T.textMuted }} />
            <Typography
              title={getLRNumbers(row)}
              sx={{
                fontSize: "0.62rem",
                color: T.textSub,
                fontWeight: 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              LR No: {getLRNumbers(row)}
            </Typography>
          </Box>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}
          >
            <CalendarToday sx={{ fontSize: 10, color: T.textMuted }} />
            <Typography sx={{ fontSize: "0.58rem", color: T.textMuted }}>
              {formatDate(row.created_at)}
            </Typography>
          </Box>
          <Chip
            label={isSettled ? "Settled" : isNegative ? "Overpaid" : "Pending"}
            size="small"
            sx={{
              ...sq,
              alignSelf: "flex-start",
              bgcolor: `${accentColor}14`,
              color: accentColor,
              fontSize: "0.48rem",
              height: 15,
              border: `1px solid ${accentColor}30`,
            }}
          />
        </Box>

        {/* Amount + toggle */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-end",
            px: 1,
            py: 0.75,
            minWidth: 100,
          }}
        >
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{ fontSize: "0.48rem", color: T.textMuted, fontWeight: 600 }}
            >
              Remaining
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.8rem",
                color: isNegative ? T.red : isSettled ? T.success : T.warning,
              }}
            >
              {formatCurrency(row.rem_amount)}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={onToggle}
            startIcon={
              expanded ? (
                <VisibilityOff sx={{ fontSize: 12 }} />
              ) : (
                <Visibility sx={{ fontSize: 12 }} />
              )
            }
            sx={{
              ...ghostBtn,
              height: 22,
              fontSize: "0.55rem",
              px: 0.7,
              "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
            }}
          >
            {expanded ? "Hide" : "View"}
          </Button>
        </Box>
      </Box>

      {/* Expanded detail */}
      {expanded && (
        <Box
          sx={{
            bgcolor: T.primaryBg,
            borderTop: `1.5px solid ${T.border}`,
            p: 1.25,
          }}
        >
          <Stack spacing={0.75}>
            {/* Amounts */}
            <Box
              sx={{
                p: 0.75,
                bgcolor: T.white,
                border: `1px solid ${T.border}`,
                ...sq,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: T.primary,
                  mb: 0.4,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                💳 Payment Summary
              </Typography>
              <Grid container spacing={0.5}>
                {[
                  ["Total", formatCurrency(row.total_amount), null],
                  ["Paid", formatCurrency(row.paid_amount), T.success],
                  [
                    "Remaining",
                    formatCurrency(row.rem_amount),
                    isNegative ? T.red : T.warning,
                  ],
                ].map(([l, v, c]) => (
                  <Grid item xs={4} key={l}>
                    <LabelValue label={l} value={v} color={c} />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* LR IDs */}
            {row.freights?.length > 0 && (
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    color: T.primary,
                    mb: 0.4,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  🚛 LR IDs
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {row.freights.map((f, i) => (
                    <Chip
                      key={i}
                      label={`#${f.freight_id}`}
                      size="small"
                      sx={{
                        ...sq,
                        bgcolor: T.primaryBg,
                        color: T.primary,
                        fontSize: "0.55rem",
                        height: 18,
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Transactions */}
            {row.payments?.length > 0 && (
              <Box
                sx={{
                  p: 0.75,
                  bgcolor: `${T.success}08`,
                  border: `1px solid ${T.success}30`,
                  ...sq,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    color: T.success,
                    mb: 0.4,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  💰 Transactions ({row.payments.length})
                </Typography>
                <Stack spacing={0.5}>
                  {row.payments.map((p, i) => (
                    <Box
                      key={i}
                      sx={{
                        p: 0.5,
                        bgcolor: T.white,
                        borderLeft: `2px solid ${T.success}`,
                        ...sq,
                      }}
                    >
                      <Grid container spacing={0.5}>
                        <Grid item xs={6}>
                          <LabelValue
                            label="Amount"
                            value={`₹${p.pay_amount}`}
                            color={T.success}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <LabelValue label="Mode" value={p.pay_mode || "—"} />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Actions */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                pt: 0.75,
                borderTop: `1px dashed ${T.border}`,
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <Button
                size="small"
                startIcon={<Visibility sx={{ fontSize: 13 }} />}
                onClick={() => onView(row)}
                sx={{
                  ...sq,
                  bgcolor: `${T.purple}14`,
                  color: T.purple,
                  fontSize: "0.65rem",
                  textTransform: "none",
                  px: 1,
                  boxShadow: "none",
                  "&:hover": { bgcolor: `${T.purple}28`, boxShadow: "none" },
                }}
              >
                View
              </Button>
              <Button
                size="small"
                startIcon={<PrintIcon sx={{ fontSize: 13 }} />}
                onClick={() => onPrint(row)}
                sx={{
                  ...sq,
                  bgcolor: `${T.warning}14`,
                  color: T.warning,
                  fontSize: "0.65rem",
                  textTransform: "none",
                  px: 1,
                  boxShadow: "none",
                  "&:hover": { bgcolor: `${T.warning}28`, boxShadow: "none" },
                }}
              >
                Print
              </Button>
            </Box>
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

// ── Main Component ─────────────────────────────────────────────────
export default function FreightPaymentReport() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [freightIdFilter, setFreightIdFilter] = useState({
    lrNumber: "",
    created_at: "",
  });
  const debouncedLrNumber = useDebounce(freightIdFilter.lrNumber, 500);
  const debouncedDate = useDebounce(freightIdFilter.created_at, 500);
  const [freightPaymentList, setFreightPayList] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [expandedCardId, setExpandedCardId] = useState(null);

  const handleCardToggle = (id) =>
    setExpandedCardId((p) => (p === id ? null : id));

  const fetchFreightpayment = useCallback(
    async (pageNo = 1, activeFilters = freightIdFilter) => {
      if (loading) return;
      setLoading(true);
      try {
        const response = await freightPayListReports(pageNo, activeFilters);
        const results = response?.results || [];
        if (pageNo === 1) {
          setFreightPayList(results);
          setPage(2);
        } else {
          setFreightPayList((p) => [...p, ...results]);
          setPage(pageNo + 1);
        }
        setHasMore(!!response?.next);
      } catch {
        setSnackbar({
          open: true,
          message: "Failed to fetch data",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    },
    [freightIdFilter, loading],
  );

  useEffect(() => {
    fetchFreightpayment(1, {
      lrNumber: debouncedLrNumber,
      created_at: debouncedDate,
    });
    setExpandedCardId(null);
  }, [debouncedLrNumber, debouncedDate]);

  const fetchMoreData = () => {
    if (!loading && hasMore) fetchFreightpayment(page, freightIdFilter);
  };
  const handleFilterChange = (k, v) =>
    setFreightIdFilter((p) => ({ ...p, [k]: v }));

  const clearFilters = () =>
    setFreightIdFilter({ lrNumber: "", created_at: "" });

  const handleView = (row) => {
    setSelectedRow(row);
    setViewOpen(true);
  };

  const handlePrintSingleRow = async (row) => {
    const printData = await paymentPrint(row.freight_payment_id);
    const w = window.open("", "", "width=900,height=600");
    row.payments = printData[0]?.payments || [];
    row.freights = printData[0]?.freights || [];
    const fHTML = row.freights?.length
      ? `<table border="1" style="border-collapse:collapse;width:100%;margin-top:20px" cellpadding="5">
          <thead><tr><th>ID</th><th>Truck</th><th>Load Wt</th><th>Unload Wt</th><th>Net Pay</th><th>Date</th></tr></thead>
          <tbody>${row.freights.map((f) => `<tr><td>${f.freight_id || ""}</td><td>${f.truck_no || ""}</td><td>${f.loading_wt || ""}</td><td>${f.received_wt || ""}</td><td>${f.net_payable || ""}</td><td>${formatDate(f.created_at)}</td></tr>`).join("")}</tbody>
         </table>`
      : "";
    const pHTML = row.payments?.length
      ? `<table border="1" style="border-collapse:collapse;width:100%;margin-top:20px" cellpadding="5">
          <thead><tr><th>Amount</th><th>Mode</th><th>Ref</th><th>To</th><th>Remark</th></tr></thead>
          <tbody>${row.payments.map((p) => `<tr><td>${p.pay_amount || ""}</td><td>${p.pay_mode || ""}</td><td>${p.upi_check_no || ""}</td><td>${p.payment_to || ""}</td><td>${p.remark || ""}</td></tr>`).join("")}</tbody>
         </table>`
      : "";
    w.document.write(`<html><head><title>Print</title>
      <style>body{font-family:Arial;padding:20px}header{text-align:center;font-size:20px;font-weight:bold}
      footer{position:fixed;bottom:10px;width:100%;text-align:center;font-size:12px}</style></head>
      <body><header>SR ENTERPRISES</header>
      <table border="1" style="border-collapse:collapse" cellpadding="5">
      <tr><td><strong>Ashutosh Ku. Singh</strong></td><td><strong>Payment ID: ${row.freight_payment_id}</strong></td></tr>
      <tr><td><strong>Branch: ${row.branch_name}</strong></td><td><strong>Date: ${formatDate(row.created_at)}</strong></td></tr>
      </table>${fHTML}${pHTML}
      <footer>Powered by: YASHVITECH IT SOLUTION PVT LTD</footer>
      <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();}}</script>
      </body></html>`);
    w.document.close();
    w.print();
  };

  const handleExportExcel = async () => {
    const XLSX = await import("xlsx-js-style");

    const headers = [
      "Sr No",
      "LR No",
      "Party Name",
      "Total",
      "Paid",
      "Remaining",
      "Date",
    ];

    const rows = freightPaymentList.map((r, i) => [
      i + 1,
      getLRNumbers(r),
      getPartyNames(r),
      r.total_amount ?? "—",
      r.paid_amount ?? "—",
      r.rem_amount ?? "—",
      formatDate(r.created_at),
    ]);

    // ── Style Definitions ──────────────────────────────────────────
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10, name: "Segoe UI" },
      fill: { fgColor: { rgb: "4F46E5" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: false },
      border: {
        top: { style: "thin", color: { rgb: "3730A3" } },
        bottom: { style: "thin", color: { rgb: "3730A3" } },
        left: { style: "thin", color: { rgb: "3730A3" } },
        right: { style: "thin", color: { rgb: "3730A3" } },
      },
    };

    const cellStyleEven = {
      font: { sz: 10, name: "Segoe UI", color: { rgb: "1E1B4B" } },
      fill: { fgColor: { rgb: "F5F3FF" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: false },
      border: {
        top: { style: "thin", color: { rgb: "E0E7FF" } },
        bottom: { style: "thin", color: { rgb: "E0E7FF" } },
        left: { style: "thin", color: { rgb: "E0E7FF" } },
        right: { style: "thin", color: { rgb: "E0E7FF" } },
      },
    };

    const cellStyleOdd = {
      ...cellStyleEven,
      fill: { fgColor: { rgb: "FFFFFF" } },
    };

    const numberStyle = {
      ...cellStyleEven,
      font: { ...cellStyleEven.font, bold: true, color: { rgb: "4F46E5" } },
    };

    const numberStyleOdd = {
      ...numberStyle,
      fill: { fgColor: { rgb: "FFFFFF" } },
    };

    // ── Build Worksheet Data ───────────────────────────────────────
    const wsData = [
      headers.map((h) => ({ v: h, t: "s", s: headerStyle })),
      ...rows.map((row, rowIdx) =>
        row.map((val, colIdx) => {
          const isEven = rowIdx % 2 === 0;
          const isNumber = colIdx === 0;

          let style;
          if (isNumber) {
            style = isEven ? numberStyle : numberStyleOdd;
          } else {
            style = isEven ? cellStyleEven : cellStyleOdd;
          }

          return {
            v: val ?? "—",
            t: typeof val === "number" ? "n" : "s",
            s: style,
          };
        }),
      ),
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // ── Column Widths (no wrapping, width based on longest content) ──
    const colWidths = headers.map((_, colIdx) => {
      const allValues = [
        headers[colIdx],
        ...rows.map((r) => String(r[colIdx] ?? "")),
      ];
      const maxLen = Math.max(...allValues.map((v) => v.length));
      return { wch: maxLen + 4 };
    });

    ws["!cols"] = colWidths;

    // ── Row Heights ────────────────────────────────────────────────
    ws["!rows"] = [{ hpt: 18 }];

    // ── Freeze Header Row ──────────────────────────────────────────
    ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2" };

    const wb = XLSX.utils.book_new();
    wb.Props = {
      Title: "Payment Report",
      Author: "Staff Management System",
      CreatedDate: new Date(),
    };

    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(
      wb,
      `Payment_Report_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  const handlePrint = () => {
    const w = window.open("", "_blank");
    w.document.write(`
    <html><head><title>Payment Report</title>
    <style>
      body { font-family: Arial; padding: 20px; }
      h2, h4 { text-align: center; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #ddd; padding: 8px; text-align: center; }
      th { background: #4f46e5; color: white; }
      tr:nth-child(even) td { background: #f5f3ff; }
    </style></head>
    <body>
      <h2>PARAS ROAD LINES</h2>
      <h4>LR Payment Report</h4>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>LR No</th>
            <th>Party Name</th>
            <th>Total</th>
            <th>Paid</th>
            <th>Remaining</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          ${freightPaymentList
            .map(
              (r, i) => `
            <tr>
              <td>${i + 1}</td>
              <td>${getLRNumbers(r)}</td>
              <td>${getPartyNames(r)}</td>
              <td>${formatCurrency(r.total_amount)}</td>
              <td>${formatCurrency(r.paid_amount)}</td>
              <td>${formatCurrency(r.rem_amount)}</td>
              <td>${formatDate(r.created_at)}</td>
            </tr>`,
            )
            .join("")}
        </tbody>
      </table>
    </body></html>
  `);
    w.document.close();
    w.print();
    w.close();
  };

  const totals = {
    total_amount: freightPaymentList.reduce(
      (s, r) => s + (r.total_amount || 0),
      0,
    ),
    paid_amount: freightPaymentList.reduce(
      (s, r) => s + (r.paid_amount || 0),
      0,
    ),
    rem_amount: freightPaymentList.reduce((s, r) => s + (r.rem_amount || 0), 0),
  };

  const EmptyState = ({ message }) => (
    <Box
      sx={{
        textAlign: "center",
        py: 8,
        border: `1.5px dashed ${T.border}`,
        bgcolor: T.primaryBg,
        ...sq,
      }}
    >
      <ReceiptIcon sx={{ fontSize: 44, color: T.border, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: T.text, fontSize: "0.9rem" }}>
        {message}
      </Typography>
    </Box>
  );

  // ─────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{ px: 0, pt: 0, mt: -3, maxWidth: 1400, mx: "auto", width: "100%" }}
    >
      {/* ── Stat Cards ── */}
      <Box sx={{ mt: 2, mb: 1.5, width: "100%" }}>
        <Grid
          container
          spacing={0.75}
          sx={{
            mx: 0,
            width: "100%",
            "& > .MuiGrid-item": { px: "3px !important", py: "3px !important" },
          }}
        >
          {[
            {
              title: "Total Amount",
              value: formatCurrency(totals.total_amount),
              color: T.primary,
            },
            {
              title: "Paid Amount",
              value: formatCurrency(totals.paid_amount),
              color: T.success,
            },
            {
              title: "Remaining",
              value: formatCurrency(totals.rem_amount),
              color: T.red,
            },
          ].map((s) => (
            <Grid item xs={4} key={s.title}>
              <StatCard {...s} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── Toolbar ── */}
      <Box sx={{ mb: 1.5 }}>
        <Grid container spacing={0.75} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="LR No..."
              value={freightIdFilter.lrNumber}
              onChange={(e) => handleFilterChange("lrNumber", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ReceiptIcon sx={{ fontSize: 14, color: T.textMuted }} />
                  </InputAdornment>
                ),
              }}
              sx={filterInputSx}
            />
          </Grid>
          {/* Date filter */}
          <Grid item xs={6} sm={3} md={3}>
            <TextField
              fullWidth
              type="date"
              size="small"
              label="Payment Date"
              InputLabelProps={{ shrink: true }}
              value={freightIdFilter.created_at}
              onChange={(e) => handleFilterChange("created_at", e.target.value)}
              sx={filterInputSx}
            />
          </Grid>

          {/* Search */}
          <Grid item xs="auto">
            <Tooltip title="Search" arrow>
              <Button
                size="small"
                onClick={() =>
                  fetchFreightpayment(1, {
                    lrNumber: debouncedLrNumber,
                    created_at: debouncedDate,
                  })
                }
                disabled={loading}
                sx={{ ...primaryBtn, height: 34, minWidth: 34, px: 1 }}
              >
                {loading ? (
                  <CircularProgress size={14} sx={{ color: T.white }} />
                ) : (
                  <SearchIcon sx={{ fontSize: 15 }} />
                )}
              </Button>
            </Tooltip>
          </Grid>

          {/* Spacer */}
          <Grid
            item
            xs={false}
            md
            sx={{ display: { xs: "none", md: "block" } }}
          />

          {/* Clear */}
          <Grid item xs="auto">
            <Tooltip title="Clear Filters" arrow>
              <span>
                <Button
                  size="small"
                  onClick={clearFilters}
                  sx={{ ...dangerBtn, height: 34, minWidth: 34, px: 1 }}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </Button>
              </span>
            </Tooltip>
          </Grid>

          {/* Export */}
          <Grid item xs="auto">
            <Tooltip title="Export Excel" arrow>
              <Button
                size="small"
                onClick={handleExportExcel}
                sx={{ ...iconBtn, height: 34, minWidth: 34, px: 1 }}
              >
                <DownloadIcon sx={{ fontSize: 15 }} />
              </Button>
            </Tooltip>
          </Grid>

          {/* Print */}
          <Grid item xs="auto">
            <Tooltip title="Print" arrow>
              <Button
                size="small"
                onClick={handlePrint}
                sx={{ ...iconBtn, height: 34, minWidth: 34, px: 1 }}
              >
                <PrintIcon sx={{ fontSize: 15 }} />
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* ── Mobile View ── */}
      {isMobile ? (
        <Box>
          {loading && !freightPaymentList.length ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 10,
                gap: 2,
              }}
            >
              <CircularProgress sx={{ color: T.primary }} size={32} />
              <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>
                Loading…
              </Typography>
            </Box>
          ) : !freightPaymentList.length ? (
            <EmptyState message="No payments found" />
          ) : (
            <Box
              id="pay-report-mobile"
              sx={{ maxHeight: "calc(100vh - 320px)", overflow: "auto" }}
            >
              <InfiniteScroll
                dataLength={freightPaymentList.length}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={
                  <Box sx={{ textAlign: "center", py: 1 }}>
                    <CircularProgress size={20} sx={{ color: T.primary }} />
                  </Box>
                }
                scrollableTarget="pay-report-mobile"
              >
                {freightPaymentList.map((r, i) => (
                  <MobilePaymentCard
                    key={r.freight_payment_id || i}
                    row={r}
                    idx={i}
                    expanded={expandedCardId === (r.freight_payment_id || i)}
                    onToggle={() => handleCardToggle(r.freight_payment_id || i)}
                    onView={handleView}
                    onPrint={handlePrintSingleRow}
                  />
                ))}
              </InfiniteScroll>

              {/* Mobile summary footer */}
              <Paper
                elevation={0}
                sx={{
                  ...sq,
                  border: `1.5px solid ${T.border}`,
                  bgcolor: T.primaryLight,
                  p: 1.5,
                  mt: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    color: T.primary,
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  📊 Grand Summary
                </Typography>
                <Grid container spacing={0.75}>
                  {[
                    [
                      "Total Amount",
                      formatCurrency(totals.total_amount),
                      T.primary,
                    ],
                    ["Paid", formatCurrency(totals.paid_amount), T.success],
                    ["Remaining", formatCurrency(totals.rem_amount), T.red],
                  ].map(([l, v, c]) => (
                    <Grid item xs={4} key={l}>
                      <Typography
                        sx={{
                          fontSize: "0.58rem",
                          color: T.textMuted,
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        {l}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.75rem", fontWeight: 700, color: c }}
                      >
                        {v}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Box>
          )}
        </Box>
      ) : (
        /* ── Desktop View ── */
        <Paper
          elevation={0}
          sx={{
            ...sq,
            border: `1.5px solid ${T.border}`,
            overflow: "hidden",
            boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
            height: "calc(100vh - 240px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TableContainer
            id="payment-report-container"
            sx={{ flex: 1, minHeight: 0, overflow: "auto" }}
          >
            <InfiniteScroll
              dataLength={freightPaymentList.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                <Box sx={{ textAlign: "center", py: 1 }}>
                  <CircularProgress size={20} sx={{ color: T.primary }} />
                </Box>
              }
              scrollableTarget="payment-report-container"
              style={{ overflow: "visible" }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  borderCollapse: "collapse",
                  "& td, & th": {
                    py: 0,
                    px: 0.75,
                    fontSize: "0.75rem",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                  },
                  "& td": {
                    borderBottom: `1px solid #ccc`,
                    height: 26,
                  },
                  "& th": {
                    fontWeight: 700,
                    color: T.headerText,
                    bgcolor: `${T.headerBg} !important`,
                    py: 0.2,
                    height: 28,
                    fontSize: "0.8rem",
                    letterSpacing: "0.01em",
                    borderBottom: `1px solid #ccc`,
                    lineHeight: 1.2,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      { l: "#", w: 35, sticky: true, left: 0 },
                      { l: "LR No", w: 120, sticky: true, left: 35 },
                      { l: "Party Name", w: 170, sticky: true, left: 155 },
                      { l: "Total Amt", w: 100 },
                      { l: "Paid Amt", w: 100 },
                      { l: "Remaining", w: 100, highlight: true },
                      { l: "Pay Date", w: 95 },
                      { l: "Actions", w: 80, stickyRight: true },
                    ].map(({ l, w, sticky, left, highlight, stickyRight }) => (
                      <TableCell
                        key={l}
                        sx={{
                          minWidth: w,
                          bgcolor: `${T.headerBg} !important`,
                          color: highlight
                            ? `${T.warning} !important`
                            : `${T.headerText} !important`,
                          ...(sticky && {
                            position: "sticky",
                            left,
                            zIndex: 12,
                          }),
                          ...(stickyRight && {
                            position: "sticky",
                            right: 0,
                            zIndex: 12,
                          }),
                        }}
                      >
                        {l}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading && !freightPaymentList.length ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        sx={{ textAlign: "center", py: 6, border: "none" }}
                      >
                        <CircularProgress sx={{ color: T.primary }} size={32} />
                      </TableCell>
                    </TableRow>
                  ) : !freightPaymentList.length ? (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        sx={{ textAlign: "center", py: 6, border: "none" }}
                      >
                        <ReceiptIcon
                          sx={{
                            fontSize: 40,
                            color: T.border,
                            mb: 1,
                            display: "block",
                            mx: "auto",
                          }}
                        />
                        <Typography
                          sx={{
                            color: T.textSub,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                          }}
                        >
                          No records found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {freightPaymentList.map((row, idx) => {
                        const isNegative = row.rem_amount < 0;
                        const isEven = idx % 2 === 0;
                        const rowBg = isEven ? T.baseRow : T.altRow;
                        const sc = {
                          bgcolor: `${rowBg} !important`,
                          zIndex: 5,
                        };

                        return (
                          <TableRow
                            key={row.freight_payment_id || idx}
                            sx={{
                              "& > td": {
                                bgcolor: rowBg,
                                transition: "background-color 0.12s",
                              },
                              "&:hover > td": {
                                bgcolor: `${T.rowHover} !important`,
                              },
                            }}
                          >
                            {/* Sticky: # */}
                            <TableCell
                              sx={{ position: "sticky", left: 0, ...sc }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 700,
                                  color: T.textMuted,
                                }}
                              >
                                {idx + 1}
                              </Typography>
                            </TableCell>

                            {/* Sticky: LR No */}
                            <TableCell
                              sx={{ position: "sticky", left: 35, ...sc }}
                            >
                              <Tooltip title={getLRNumbers(row)} arrow>
                                <Typography
                                  sx={{
                                    cursor: "pointer",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    color: T.primary,
                                  }}
                                >
                                  {getLRDisplay(row)}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            {/* Sticky: Party Name */}
                            <TableCell
                              sx={{ position: "sticky", left: 155, ...sc }}
                            >
                              <Tooltip title={getPartyNames(row)} arrow>
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    color: T.text,
                                    fontSize: "0.75rem",
                                    maxWidth: 150,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    cursor: "pointer",
                                  }}
                                >
                                  {getPartyNames(row)}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            {/* Total Amt */}
                            <TableCell
                              sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                            >
                              {formatCurrency(row.total_amount)}
                            </TableCell>

                            {/* Paid Amt */}
                            <TableCell
                              sx={{
                                color: T.success,
                                fontWeight: 600,
                                fontSize: "0.75rem",
                              }}
                            >
                              {formatCurrency(row.paid_amount)}
                            </TableCell>

                            {/* Remaining */}
                            <TableCell>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.75rem",
                                  color: isNegative ? T.red : T.success,
                                }}
                              >
                                {formatCurrency(row.rem_amount)}
                              </Typography>
                            </TableCell>

                            {/* Date */}
                            <TableCell sx={{ fontSize: "0.75rem" }}>
                              {formatDate(row.created_at)}
                            </TableCell>

                            {/* Sticky: Actions */}
                            <TableCell
                              sx={{
                                position: "sticky",
                                right: 0,
                                ...sc,
                                py: "2px !important",
                              }}
                            >
                              <Stack
                                direction="row"
                                spacing={0.5}
                                justifyContent="center"
                              >
                                <ActionButton
                                  tip="View"
                                  icon={<Visibility />}
                                  isView
                                  onClick={() => handleView(row)}
                                />
                                <ActionButton
                                  tip="Print"
                                  icon={<PrintIcon />}
                                  isPrint
                                  onClick={() => handlePrintSingleRow(row)}
                                />
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {/* Totals Row */}
                      {/* Totals Row */}
                      <TableRow
                        sx={{
                          position: "sticky",
                          bottom: 0,
                          zIndex: 20,
                          "& td": {
                            bgcolor: T.successBg,
                            fontWeight: 600,
                            fontSize: "0.68rem",
                            py: 0.5,
                            borderTop: `2px solid ${T.primary}`,
                            borderBottom: "none",
                          },
                        }}
                      >
                        {/* # */}
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 0,
                            zIndex: 22,
                            bgcolor: `${T.successBg} !important`,
                          }}
                        >
                          —
                        </TableCell>

                        {/* LR No */}
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 35,
                            zIndex: 22,
                            bgcolor: `${T.successBg} !important`,
                          }}
                        />

                        {/* Party Name / Totals label */}
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 155,
                            zIndex: 22,
                            color: T.primary,
                            fontWeight: 800,
                            bgcolor: `${T.successBg} !important`,
                          }}
                        >
                          Totals
                        </TableCell>

                        {/* Total Amt */}
                        <TableCell sx={{ fontWeight: 700 }}>
                          {formatCurrency(totals.total_amount)}
                        </TableCell>

                        {/* Paid Amt */}
                        <TableCell sx={{ color: T.success, fontWeight: 700 }}>
                          {formatCurrency(totals.paid_amount)}
                        </TableCell>

                        {/* Remaining */}
                        <TableCell sx={{ color: T.primary, fontWeight: 800 }}>
                          {formatCurrency(totals.rem_amount)}
                        </TableCell>

                        {/* Date */}
                        <TableCell />

                        {/* Actions */}
                        <TableCell
                          sx={{
                            position: "sticky",
                            right: 0,
                            zIndex: 22,
                            bgcolor: `${T.successBg} !important`,
                          }}
                        />
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </InfiniteScroll>
          </TableContainer>
        </Paper>
      )}

      {/* ── View Dialog ── */}
      <Dialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            ...sq,
            border: `1.5px solid ${T.border}`,
            boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
            overflow: "hidden",
          },
        }}
      >
        {/* Dialog Header */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            bgcolor: T.primary,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "4px",
                bgcolor: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ReceiptIcon sx={{ color: T.white, fontSize: 16 }} />
            </Box>
            <Typography
              sx={{ fontWeight: 700, color: T.white, fontSize: "0.9rem" }}
            >
              Payment Details
            </Typography>
          </Stack>
          <IconButton
            onClick={() => setViewOpen(false)}
            size="small"
            sx={{
              color: T.white,
              borderRadius: "4px",
              "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 2.5 }}>
          {selectedRow && (
            <>
              {/* Summary grid */}
              <Box
                sx={{
                  p: 1.5,
                  ...sq,
                  bgcolor: T.primaryBg,
                  border: `1px solid ${T.border}`,
                  mb: 2,
                }}
              >
                <Grid container spacing={1.5}>
                  {[
                    ["LR No", getLRNumbers(selectedRow), T.primary],
                    ["Party Name", getPartyNames(selectedRow), null],
                    [
                      "Total",
                      formatCurrency(selectedRow.total_amount),
                      T.primary,
                    ],
                    [
                      "Paid",
                      formatCurrency(selectedRow.paid_amount),
                      T.success,
                    ],
                    [
                      "Remaining",
                      formatCurrency(selectedRow.rem_amount),
                      selectedRow.rem_amount < 0 ? T.red : T.warning,
                    ],
                    ["Date", formatDate(selectedRow.created_at), null],
                  ].map(([l, v, c]) => (
                    <Grid item xs={6} key={l}>
                      <LabelValue label={l} value={v} color={c} />
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Transactions table */}
              {selectedRow?.payments?.length > 0 && (
                <>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      color: T.primary,
                      mb: 1,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Transactions
                  </Typography>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ ...sq, border: `1px solid ${T.border}` }}
                  >
                    <Table
                      size="small"
                      sx={{
                        "& th": {
                          fontWeight: 700,
                          color: T.headerText,
                          bgcolor: `${T.headerBg} !important`,
                          fontSize: "0.8rem",
                          letterSpacing: "0.01em",
                          py: 0.2,
                          height: 28,
                          borderBottom: `1px solid #ccc`,
                        },
                        "& td": {
                          fontSize: "0.75rem",
                          borderBottom: `1px solid #ccc`,
                          py: 0.2,
                          height: 26,
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          {/* Transaction header cells */}
                          {["Amount", "Mode", "To", "Ref", "Remark"].map(
                            (h) => (
                              <TableCell
                                key={h}
                                sx={{
                                  bgcolor: `${T.headerBg} !important`,
                                  color: `${T.headerText} !important`,
                                }}
                              >
                                {h}
                              </TableCell>
                            ),
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedRow.payments.map((p, i) => (
                          <TableRow
                            key={i}
                            sx={{
                              "& > td": {
                                bgcolor: i % 2 === 0 ? T.baseRow : T.altRow,
                              },
                              "&:hover > td": {
                                bgcolor: `${T.rowHover} !important`,
                              },
                            }}
                          >
                            <TableCell
                              sx={{ color: T.success, fontWeight: 600 }}
                            >
                              {formatCurrency(p.pay_amount)}
                            </TableCell>
                            <TableCell>{p.pay_mode}</TableCell>
                            <TableCell>{p.payment_to}</TableCell>
                            <TableCell>{p.upi_check_no}</TableCell>
                            <TableCell>{p.remark}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            bgcolor: T.primaryBg,
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <Button
            onClick={() => setViewOpen(false)}
            sx={{ ...ghostBtn, px: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          top: "80px !important",
        }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ ...sq, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
